"use client"

import {
  Box,
  Container,
  Typography,
  Avatar,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Card,
  CardContent,
  Chip,
} from "@mui/material"
import {
  Email as EmailIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Badge as BadgeIcon,
} from "@mui/icons-material"
import { useAuth } from "../context/AuthContext"
import { getUserRoleFromToken, formatRoleName, getRoleColor, getRoleIcon } from "../utils/authUtils"
import { motion } from "framer-motion"
import { useState, useEffect } from "react"

const Profile = () => {
  const { user } = useAuth()
  const [userRole, setUserRole] = useState("employee")

  // Get user role from token on component mount
  useEffect(() => {
    const role = getUserRoleFromToken()
    setUserRole(role)
  }, [])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  }

  return (
    <Container maxWidth="lg">
      <motion.div variants={containerVariants} initial="hidden" animate="visible">
        {/* Enhanced Header */}
        <motion.div variants={itemVariants}>
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              mb: 4,
              p: 4,
              background: "linear-gradient(135deg, #2c3e50 0%, #34495e 50%, #3d566e 100%)",
              borderRadius: 4,
              color: "white",
              textAlign: "center",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "radial-gradient(circle at 30% 70%, rgba(255,255,255,0.15) 0%, transparent 50%)",
                pointerEvents: "none",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                top: "-50%",
                left: "-50%",
                width: "200%",
                height: "200%",
                background: "conic-gradient(from 45deg, transparent, rgba(255,255,255,0.08), transparent)",
                animation: "rotate 20s linear infinite",
                pointerEvents: "none",
              },
            }}
          >
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 120 }}

              >
                <Avatar
                  src={user?.picture}
                  alt={user?.name}
                  sx={{
                    width: 120,
                    height: 120,
                    mx: "auto",
                    mb: 3,
                    background: "linear-gradient(135deg, #20d5d5 0%, #16B1C8 100%)",
                    fontSize: "48px",
                    fontWeight: 700,
                    boxShadow: "0 12px 40px rgba(0,0,0,0.2)",
                    border: "4px solid rgba(255,255,255,0.3)",
                  }}
                >
                  {user?.name?.charAt(0)}
                </Avatar>
              </motion.div>
              <Typography
                variant="h3"
                fontWeight={800}
                gutterBottom
                sx={{
                  background: "linear-gradient(45deg, #fff 30%, #e8f5e9 90%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {user?.name || "User Profile"}
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
                {user?.email}
              </Typography>
              <Chip
                label={`${getRoleIcon(userRole)} ${formatRoleName(userRole)}`}
                sx={{
                  background: "linear-gradient(135deg, rgba(32, 213, 213, 0.25) 0%, rgba(32, 213, 213, 0.15) 100%)",
                  color: "white",
                  fontWeight: 700,
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255,255,255,0.3)",
                }}
              />
            </Box>
          </Box>
        </motion.div>

        <Grid container spacing={4}>
          {/* User Information Card - Full Width */}
          <Grid item xs={12}>
            <motion.div variants={itemVariants}>
              <Card
                sx={{
                  borderRadius: 4,
                  background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255,255,255,0.3)",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.1)",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    background: "linear-gradient(135deg, #20d5d5 0%, #16B1C8 100%)",
                    p: 3,
                    color: "white",
                  }}
                >
                  <Typography variant="h5" fontWeight={700} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <PersonIcon sx={{ fontSize: 28 }} />👤 User Information
                  </Typography>
                </Box>
                <CardContent sx={{ p: 0 }}>
                  <List sx={{ py: 0 }}>
                    <motion.div whileHover={{ backgroundColor: "rgba(255, 128, 0, 0.05)" }}>
                      <ListItem sx={{ py: 3, px: 4 }}>
                        <ListItemIcon>
                          <motion.div
                            animate={{ rotate: [0, 10, -10, 0] }}
                            transition={{ duration: 3, repeat: Number.POSITIVE_INFINITY }}
                          >
                            <EmailIcon sx={{ color: "#20d5d5", fontSize: 28 }} />
                          </motion.div>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="h6" fontWeight={700} sx={{ color: "#20d5d5" }}>
                              📧 Email Address
                            </Typography>
                          }
                          secondary={
                            <Typography variant="body1" fontWeight={600} sx={{ mt: 1 }}>
                              {user?.email || "Not provided"}
                            </Typography>
                          }
                        />
                      </ListItem>
                    </motion.div>

                    <Divider sx={{ mx: 4 }} />

                    <motion.div whileHover={{ backgroundColor: "rgba(75, 46, 131, 0.05)" }}>
                      <ListItem sx={{ py: 3, px: 4 }}>
                        <ListItemIcon>
                          <motion.div
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY }}
                          >
                            <PersonIcon sx={{ color: "#2c3e50", fontSize: 28 }} />
                          </motion.div>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="h6" fontWeight={700} sx={{ color: "#2c3e50" }}>
                              👤 Full Name
                            </Typography>
                          }
                          secondary={
                            <Typography variant="body1" fontWeight={600} sx={{ mt: 1 }}>
                              {user?.name || "Not provided"}
                            </Typography>
                          }
                        />
                      </ListItem>
                    </motion.div>

                    <Divider sx={{ mx: 4 }} />

                    <motion.div whileHover={{ backgroundColor: "rgba(46, 125, 50, 0.05)" }}>
                      <ListItem sx={{ py: 3, px: 4 }}>
                        <ListItemIcon>
                          <motion.div
                            animate={{ y: [0, -5, 0] }}
                            transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY }}
                          >
                            <BadgeIcon sx={{ color: getRoleColor(userRole), fontSize: 28 }} />
                          </motion.div>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="h6" fontWeight={700} sx={{ color: getRoleColor(userRole) }}>
                              {getRoleIcon(userRole)} User Role
                            </Typography>
                          }
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              <Chip
                                label={formatRoleName(userRole)}
                                sx={{
                                  background: `linear-gradient(135deg, ${getRoleColor(userRole)}20 0%, ${getRoleColor(userRole)}15 100%)`,
                                  color: getRoleColor(userRole),
                                  fontWeight: 700,
                                  borderRadius: 2,
                                  border: `1px solid ${getRoleColor(userRole)}30`,
                                }}
                              />
                            </Box>
                          }
                        />
                      </ListItem>
                    </motion.div>
                  </List>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </motion.div>

      <style jsx>{`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Container>
  )
}

export default Profile